import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Keep polling
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-lg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-[#09090b] border border-white/10 p-12 text-center"
        >
          {status === 'loading' && (
            <>
              <div className="spinner mx-auto mb-6" />
              <h1 className="font-['Teko'] text-3xl text-white mb-4">
                VÉRIFICATION DU PAIEMENT...
              </h1>
              <p className="font-['Barlow'] text-gray-400">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-[#22C55E] mx-auto mb-6 flex items-center justify-center">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h1 
                className="font-['Teko'] text-4xl text-white mb-4"
                data-testid="payment-success-title"
              >
                PAIEMENT RÉUSSI !
              </h1>
              <p className="font-['Barlow'] text-gray-400 mb-8">
                Votre inscription a été confirmée. Vous recevrez un email de confirmation 
                avec tous les détails de votre formation.
              </p>
              <div className="flex flex-col gap-4">
                <Link to="/trainings">
                  <Button className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]">
                    Voir les formations <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-20 h-20 bg-[#EF4444] mx-auto mb-6 flex items-center justify-center">
                <span className="text-white text-3xl">!</span>
              </div>
              <h1 className="font-['Teko'] text-4xl text-white mb-4">
                PROBLÈME DE VÉRIFICATION
              </h1>
              <p className="font-['Barlow'] text-gray-400 mb-8">
                Nous n'avons pas pu vérifier votre paiement. Si le montant a été débité, 
                contactez-nous avec votre numéro de session.
              </p>
              <p className="font-['Barlow'] text-gray-500 text-sm mb-6 break-all">
                Session: {sessionId}
              </p>
              <Link to="/contact">
                <Button className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-wider hover:bg-[#EAB308]">
                  Nous contacter
                </Button>
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
